import React, { useState, useRef, useMemo, useEffect } from "react";
import { StyleSheet, FlatList } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  EmptyJouvie,
  MySafeAreaView,
  MainText,
  PressableOpacity,
} from "app/components/common";
import i18n from "@microdoing/shared/i18n";
import { getStepFeedbacks } from "@microdoing/shared/services/userCampaign/selectors";
import {
  getReviewsRequest,
  selectReviewFromFeedbackId,
} from "@microdoing/shared/services/userCampaign/actions";
import { getReviewsRequesting } from "@microdoing/shared/services/userCampaign/selectors";
import { RedirectNavig } from "../../components/common/RedirectNavig";
import theme from "app/Theme/Theme";
import HeaderScreen from "app/components/common/HeaderScreen";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { breadCrumbsActionAfest } from "app/navigation/breadcrumb/BreadcrumbPaths";
import { useNavigation } from "@react-navigation/native";
import HistoricalBox from "./HistoricalBox";
import normalize from "app/utils/normalize";

const styles = StyleSheet.create({
  flatListContainer: {
    width: "100%",
    height: "100%",
  },
});

interface IProps {}

const FilterElement = ({
  optionKey,
  label,
  isSelected,
  setSelectedElement,
}) => {
  const [textWidth, setTextWidth] = useState(0);

  const handleTextLayout = event => {
    setTextWidth(event.nativeEvent.layout.width);
  };

  const onPress = () => {
    setSelectedElement(optionKey);
  };
  return (
    <PressableOpacity
      style={{
        height: "100%",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
      onPress={onPress}
    >
      <MainText
        variant={isSelected ? "l4-bold" : "l4-medium"}
        color={isSelected ? "brand.500" : "neutral.600"}
        onLayout={handleTextLayout}
        marginRight="sm"
      >
        {label}
      </MainText>
      {isSelected && (
        <Box backgroundColor="brand.500" height={2} width={textWidth} />
      )}
    </PressableOpacity>
  );
};

const SearchingFilters = ({ options, selectedElement, setSelectedElement }) => {
  return (
    <Box width="100%" height={normalize(30)} flexDirection="row">
      {options.map(option => (
        <FilterElement
          key={option.key}
          optionKey={option.key}
          label={option.label}
          isSelected={selectedElement === option.key}
          setSelectedElement={setSelectedElement}
        />
      ))}
    </Box>
  );
};

const HistoricalList = ({}: IProps) => {
  const [tabSelected, setTabSelected] = useState("");
  const reviewRequesting = useSelector(getReviewsRequesting);

  const flatListRef = useRef<FlatList>(null);
  const feedback = useSelector(getStepFeedbacks);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const handleFeedbackPress = feedback => () => {
    dispatch(selectReviewFromFeedbackId(feedback._id));
  };

  const keyExtractor = (item, index) => {
    return index.toString();
  };

  const onRefresh = () => {
    dispatch(getReviewsRequest()); // TODO Rename into getFeedbackRequest function from action saga constant
  };

  const sortedReviews = useMemo(() => {
    let reviewSorted = [...feedback];
    if (tabSelected === "new") {
      reviewSorted = reviewSorted?.filter(element => !element?.status);
    } else if (tabSelected) {
      reviewSorted = reviewSorted?.filter(
        element => element?.status === tabSelected.trim(),
      );
    }

    return reviewSorted;
  }, [tabSelected, feedback]);

  const validationOptions = [
    { label: i18n.t("all-validation-option"), key: "" },
    { label: i18n.t("status-review-new"), key: "new" },
    { label: i18n.t("status-review-toComplete"), key: "toComplete" },
    { label: i18n.t("validated-validation-option"), key: "validated" },
  ];

  useEffect(() => {
    if (sortedReviews.length > 0)
      flatListRef.current?.scrollToIndex({ index: 0, animated: true });
  }, [sortedReviews]);

  const renderItem = ({ item, index }) => (
    <HistoricalBox
      index={index}
      key={item?._id}
      feedback={item}
      onPress={handleFeedbackPress(item)}
    />
  );
  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsActionAfest}>
      <MySafeAreaView
        backgroundColor={isWebDesktop ? "neutral.75" : "neutral.white"}
        edges={["top"]}
        paddingVertical="xs"
        testID="review-screen"
        height="100%"
      >
        {!isWebDesktop && (
          <Box marginHorizontal="sm">
            <HeaderScreen leftIcon title={i18n.t("Reviews")} />
          </Box>
        )}
        <>
          <Box paddingHorizontal="sm">
            <SearchingFilters
              options={validationOptions}
              selectedElement={tabSelected}
              setSelectedElement={setTabSelected}
            />
          </Box>
          <Box backgroundColor="neutral.75" width="100%" flex={1} padding="sm">
            {sortedReviews.length > 0 ? (
              <FlatList
                style={styles.flatListContainer}
                ref={flatListRef}
                onRefresh={onRefresh}
                refreshing={reviewRequesting}
                data={sortedReviews}
                showsVerticalScrollIndicator={false}
                renderItem={renderItem}
                keyExtractor={keyExtractor}
                keyboardShouldPersistTaps="always"
              />
            ) : (
              <EmptyJouvie
                firstMessage={i18n.t("no-actions-to-check")}
                secondMessage={i18n.t("see-daily-actions")}
                onPress={() => navigation.goBack()}
              />
            )}
          </Box>
        </>
      </MySafeAreaView>
    </WebDesktopView>
  );
};

export default HistoricalList;
